import * as React from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { calendarDateSchema } from '@lib/validators/calendar-date.validator'
import type { ChangeQuestion } from '@/site-visits/siteRecordChanges'
import { todayLocal } from '@/site-visits/visitStore'

/** A day the date input produced, or none when it is blank or not a real day. */
function changedOnOf(day: string): string | undefined {
  const parsed = calendarDateSchema.safeParse(day)
  return parsed.success ? parsed.data : undefined
}

interface ChangeAnswersProps {
  question: ChangeQuestion
  /**
   * True for "it was wrong", false for "it changed"; with the day it
   * changed when `askWhen` asked it.
   */
  onAnswer: (isCorrection: boolean, changedOn?: string) => void
  /**
   * Ask when it changed, beside the answers: for a change made in no visit,
   * at a desk months after the fact, which is otherwise dated the day it is
   * saved. Today to start with, and sent with "it changed" only.
   */
  askWhen?: boolean
  /** How the first answer reads: a save, or a removal. */
  tone?: 'save' | 'remove'
  disabled?: boolean
  size?: 'default' | 'sm'
  /** For the touch-sized controls of the survey screens. */
  buttonClassName?: string
  className?: string
}

/**
 * "Was it wrong, or did it change?" as the save button itself: two buttons,
 * one per answer, in place of the one that would have saved without asking.
 * Asked on a revisit only, of an edit that could be either, so the answer
 * goes with the change into the site's history (docs/site-visits.md).
 *
 * The site changing is first and stronger: on a revisit it is the commoner
 * answer, and the one an unanswered change is taken as.
 */
export function ChangeAnswers({
  question,
  onAnswer,
  tone = 'save',
  disabled = false,
  size = 'default',
  buttonClassName,
  className,
  askWhen = false,
}: ChangeAnswersProps) {
  const [day, setDay] = React.useState(() => todayLocal())
  return (
    <span
      role="group"
      aria-label="Was it wrong, or did it change?"
      className={cn('flex flex-wrap items-center gap-2', className)}
    >
      {askWhen ? (
        <label className="flex items-center gap-1.5 text-xs text-secondary-text">
          Changed on
          <input
            type="date"
            value={day}
            max={todayLocal()}
            disabled={disabled}
            className={cn(
              'h-9 rounded-md border border-default bg-surface px-2 text-sm text-primary-text',
              buttonClassName,
            )}
            onChange={(event) => {
              setDay(event.target.value)
            }}
          />
        </label>
      ) : null}
      <Button
        type="button"
        variant={tone === 'remove' ? 'destructive' : 'default'}
        size={size}
        disabled={disabled}
        className={buttonClassName}
        onClick={() => {
          // A cleared date is no date: the change is dated the day it is saved.
          onAnswer(false, askWhen ? changedOnOf(day) : undefined)
        }}
      >
        {question.changed}
      </Button>
      <Button
        type="button"
        variant="outline"
        size={size}
        disabled={disabled}
        className={buttonClassName}
        onClick={() => {
          onAnswer(true)
        }}
      >
        {question.wrong}
      </Button>
    </span>
  )
}
